<!--
  The signed-in user's settings: titled sections of rows (SettingRow), each
  a name, what it does and its control. A new setting is a row; a new kind
  of setting, a section.
-->
<script lang="ts">
  import { onMount } from 'svelte';
  import { updateSettingsPage } from '../lib/commands/page';
  import { carryOut, useKeys } from '../lib/keys/keyboard.svelte';
  import { nav } from '../lib/nav.svelte';
  import { statusOf } from '../lib/runtime/remote';
  import { prefs } from '../lib/state/prefs.svelte';
  import { applyTheme, THEME_LABELS, THEMES, type Theme } from '../lib/theme';
  import { session } from '../lib/state/session.svelte';
  import { settings } from '../lib/state/settings.svelte';
  import SettingRow from '../lib/components/SettingRow.svelte';
  import ViewedSyncSetting from '../lib/components/ViewedSyncSetting.svelte';

  // Changed on another device meanwhile, perhaps: what is shown is the server's.
  onMount(() => settings.refresh());

  useKeys({ scope: 'settings', handle: (command) => carryOut(updateSettingsPage(null, command)[1], () => nav.back()) });

  const status = $derived(statusOf(settings.server, 'your settings'));

  function setTheme(theme: Theme) {
    prefs.set('theme', theme);
    applyTheme(theme);
  }
</script>

<div class="settings-page">
  <header class="top"><div class="eyebrow">Your account{session.user ? ` · ${session.user.login}` : ''}</div></header>
  <h1 class="section-title">Settings</h1>

  {#if status.summary}
    <p class="banner settings-status" role="status" aria-busy={status.busy}>
      {status.summary}
      {#if status.next}{status.next}{/if}
      {#if status.advice}{status.advice}{/if}
      {#if settings.server.tag === 'Failed'}<button type="button" class="btn small" onclick={() => settings.refresh()}>Try again</button>{/if}
    </p>
  {/if}

  <section class="settings-group" aria-labelledby="settings-github">
    <h2 id="settings-github">GitHub</h2>
    <p class="group-note">Kept with your account, so these are the same in every browser you sign in from.</p>
    <div class="card settings-rows">
      <ViewedSyncSetting
        choice={settings.viewedSyncChoice}
        saving={settings.saving}
        refused={settings.refused}
        health={settings.viewedHealth}
        onchange={(on) => settings.setViewedSync(on)}
      />
    </div>
  </section>

  <section class="settings-group" aria-labelledby="settings-appearance">
    <h2 id="settings-appearance">Appearance</h2>
    <p class="group-note">Kept in this browser.</p>
    <div class="card settings-rows">
      <SettingRow id="theme" name="Color theme">
        {#snippet description()}Light or dark, or whichever your system is set to. The top bar’s theme button changes the same setting.{/snippet}
        {#snippet control({ labelId, descId })}
          <div class="choices" role="radiogroup" aria-labelledby={labelId} aria-describedby={descId}>
            {#each THEMES as t (t)}
              <label><input type="radio" name="theme" value={t} checked={prefs.value.theme === t} onchange={() => setTheme(t)} /> {THEME_LABELS[t]}</label>
            {/each}
          </div>
        {/snippet}
      </SettingRow>
    </div>
  </section>
</div>
